import React, { useMemo, useState, useEffect } from "react";
import { Footer } from "../components/common";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/CheckoutPage.css";
import http from "../lib/http";
import guestHttp from "../lib/guestHttp"; // ← import the guest axios instance
import { toast } from "react-toastify";
import { LocationService } from "../services/location";
import LocationSelect from "../components/common/LocationSelect";
import { getAddresses, createAddress } from "../services/address";
import { getAuthMode, requireGuestOrUser } from "../utils/authHelpers";

/* ─── Pure helpers ────────────────────────────────────── */
const toCurrency = (n) => `$${Number(n || 0).toFixed(2)}`;
const getQty = (i) => Number(i?.quantity ?? 1);
const getUnitPrice = (i) =>
  Number(i?.unitPrice ?? i?.unit_price ?? i?.product?.price ?? i?.price ?? 0);
const getLineSubtotal = (i) => {
  const d = Number(i?.subtotal ?? i?.lineSubtotal ?? NaN);
  return !Number.isNaN(d) && d >= 0 ? d : getUnitPrice(i) * getQty(i);
};

/* ─── Weight helpers ─────────────────────────────────── */
const getUnitWeightKg = (i) => {
  const w = Number(i?.weight ?? i?.product?.weight ?? 0);
  const unit = String(
    i?.weight_unit ?? i?.product?.weight_unit ?? "kg",
  ).toLowerCase();
  if (!w || Number.isNaN(w)) return 0;
  if (unit === "g" || unit === "gram" || unit === "grams") return w / 1000;
  return w;
};
const getLineWeightKg = (i) => getUnitWeightKg(i) * getQty(i);

const toList = (r) => {
  const d = r?.data?.data ?? r?.data ?? [];
  return Array.isArray(d) ? d : [];
};
const toDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};
const getProductId = (i) =>
  i?.productId ?? i?.product_id ?? i?.product?.id ?? i?.id ?? null;
const getCartItemId = (i) => i?.cart_item_id ?? i?.cartItemId ?? i?.id ?? null;

/* ─── Shipping calc (user only) ─────────────────────── */
const GST_RATE = 0.1;

const calculateShippingFee = (weightKg, rates) => {
  if (!rates || rates.length === 0 || weightKg <= 0)
    return { fee: 0, matchedRate: null };

  const sorted = [...rates].sort((a, b) => a.min_weight - b.min_weight);
  const matchedRate = sorted.find(
    (r) => weightKg >= r.min_weight && weightKg <= r.max_weight,
  );
  if (!matchedRate) return { fee: 0, matchedRate: null };

  const fee = weightKg <= 10 ? matchedRate.price : weightKg * matchedRate.price;
  return { fee, matchedRate };
};

/* ─── Icons ───────────────────────────────────────────── */
const MapPinIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const PackageIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);
const UserIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const ReceiptIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const TagIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);
const EditIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ═══════════════════════════════════════════════════════
   CheckoutPage
═══════════════════════════════════════════════════════ */
const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const isBuyNow = location.state?.isBuyNow === true;

  // Items from cart — must include cart_item_id for guest flow
  const items = Array.isArray(location.state?.items)
    ? location.state.items
    : [];

  const [authMode, setAuthMode] = useState(() => getAuthMode());
  const isGuest = authMode === "guest";
  const isUser = authMode === "user";

  useEffect(() => {
    const sync = () => setAuthMode(getAuthMode());
    window.addEventListener("auth:changed", sync);
    window.addEventListener("storage", sync);
    sync();
    return () => {
      window.removeEventListener("auth:changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  /* ── Guest: server-side totals from /guest/cart/checkout ── */
  const [guestServerTotals, setGuestServerTotals] = useState(null);
  const [loadingGuestTotals, setLoadingGuestTotals] = useState(false);

  useEffect(() => {
    if (!isGuest || items.length === 0 || isBuyNow) return;

    const cartItemIds = items.map((i) => getCartItemId(i)).filter(Boolean);

    if (cartItemIds.length === 0) return;

    const fetchGuestCheckout = async () => {
      try {
        setLoadingGuestTotals(true);
        // Build query string: ?cart_item_ids[]=id1&cart_item_ids[]=id2
        const params = new URLSearchParams();
        cartItemIds.forEach((id) => params.append("cart_item_ids[]", id));

        const res = await guestHttp.get(
          `/api/guest/cart/checkout?${params.toString()}`,
        );
        const data = res?.data?.data;
        if (data) {
          setGuestServerTotals({
            itemsTotal: Number(data.subtotal ?? 0),
            deliveryFee: Number(data.total_shipping_amount ?? 0),
            gstAmount: Number(data.gst_amount ?? 0),
            total: Number(data.total_amount ?? 0),
            totalWeightKg: Number(data.total_weight ?? 0),
          });
        }
      } catch (e) {
        console.error("[GUEST CHECKOUT] Failed to fetch totals:", e);
        toast.error("Failed to load checkout totals. Please try again.");
      } finally {
        setLoadingGuestTotals(false);
      }
    };

    fetchGuestCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest]);

  const [guestContact, setGuestContact] = useState({
    contact_name: "",
    contact_email: "",
    contact_phone: "",
  });

  /* ── User: saved addresses ── */
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(true);

  /* ── Location dropdowns ── */
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const [form, setForm] = useState({
    country: "",
    state: "",
    city: "",
    zip: "",
    address1: "",
    address2: "",
    streetNumber: "",
    label: "home",
  });

  /* ── User: shipping rates ── */
  const [shippingRates, setShippingRates] = useState([]);
  const [loadingShipping, setLoadingShipping] = useState(false);

  /* ── User: promos + vouchers ── */
  const [availablePromoCodes, setAvailablePromoCodes] = useState([]);
  const [availableShippingVouchers, setAvailableShippingVouchers] = useState(
    [],
  );
  const [selectedPromoId, setSelectedPromoId] = useState("");
  const [selectedVoucherId, setSelectedVoucherId] = useState("");
  const [selectedPromoObj, setSelectedPromoObj] = useState(null);
  const [selectedVoucherObj, setSelectedVoucherObj] = useState(null);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [saving, setSaving] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [errors, setErrors] = useState({});

  /* ── User: shipping rates load ── */
  useEffect(() => {
    if (!isUser && !(isGuest && isBuyNow)) return;
    const load = async () => {
      try {
        setLoadingShipping(true);
        const response = await http.get("/api/customer/shipping-rates");
        const rawRates = response?.data?.data ?? response?.data ?? [];
        const arr = Array.isArray(rawRates)
          ? rawRates
          : Array.isArray(rawRates?.data)
            ? rawRates.data
            : [];

        const mapped = arr
          .map((r) => ({
            id: r.id,
            min_weight: Number(r.min_weight ?? 0),
            max_weight: Number(r.max_weight ?? 0),
            weight_range: r.weight_range ?? "",
            delivery_type: r.delivery_type ?? "normal",
            price: Number(r.price ?? 0),
          }))
          .filter((r) => r.max_weight > 0);

        setShippingRates(mapped);
      } catch (e) {
        console.error("Error loading shipping rates:", e);
        toast.error("Failed to load shipping rates");
      } finally {
        setLoadingShipping(false);
      }
    };
    load();
  }, [isUser]);

  /* ── User: promos + vouchers ── */
  useEffect(() => {
    if (!isUser) {
      setAvailablePromoCodes([]);
      setAvailableShippingVouchers([]);
      setSelectedPromoId("");
      setSelectedVoucherId("");
      return;
    }
    const loadPromos = async () => {
      try {
        const res = await http.get("/api/customer/promo-codes/applied");
        const list = toList(res).filter((p) => String(p.is_active) === "1");
        setAvailablePromoCodes(list);
        if (list.length && !selectedPromoId)
          setSelectedPromoId(String(list[0].id));
      } catch (e) {
        console.error("Error loading promo codes:", e);
        setAvailablePromoCodes([]);
      }
    };
    const loadVouchers = async () => {
      try {
        const res = await http.get("/api/customer/vouchers/applied");
        const list = toList(res).filter(
          (v) => v?.pivot?.is_collected === true && v?.pivot?.is_used === false,
        );
        setAvailableShippingVouchers(list);
        if (list.length && !selectedVoucherId)
          setSelectedVoucherId(String(list[0].id));
      } catch (e) {
        console.error("Error loading vouchers:", e);
        setAvailableShippingVouchers([]);
      }
    };
    loadPromos();
    loadVouchers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUser]);

  useEffect(() => {
    setSelectedPromoObj(
      !selectedPromoId
        ? null
        : availablePromoCodes.find(
            (p) => String(p.id) === String(selectedPromoId),
          ) || null,
    );
  }, [selectedPromoId, availablePromoCodes]);

  useEffect(() => {
    setSelectedVoucherObj(
      !selectedVoucherId
        ? null
        : availableShippingVouchers.find(
            (v) => String(v.id) === String(selectedVoucherId),
          ) || null,
    );
  }, [selectedVoucherId, availableShippingVouchers]);

  /* ── User: saved addresses load ── */
  useEffect(() => {
    if (!isUser) {
      setSavedAddresses([]);
      setSelectedAddressId("");
      setShowNewAddressForm(true);
      setLoadingAddresses(false);
      return;
    }
    const load = async () => {
      try {
        setLoadingAddresses(true);
        const r = await getAddresses();
        setSavedAddresses(r?.data?.data || []);
      } catch (e) {
        console.error("Error loading addresses:", e);
      } finally {
        setLoadingAddresses(false);
      }
    };
    load();
  }, [isUser]);

  /* ── Countries / states / cities (unchanged) ── */
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingCountries(true);
        setCountries(LocationService.getAllCountries());
      } catch (e) {
        console.error("Error loading countries:", e);
        toast.error("Failed to load countries");
      } finally {
        setLoadingCountries(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      setLoadingStates(true);
      try {
        setStates(LocationService.getStatesByCountry(selectedCountry));
        setForm((p) => ({ ...p, state: "", city: "" }));
        setSelectedState("");
        setCities([]);
      } catch (e) {
        console.error("Error loading states:", e);
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    } else {
      setStates([]);
      setCities([]);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedCountry && selectedState) {
      setLoadingCities(true);
      try {
        setCities(
          LocationService.getCitiesByCountryAndState(
            selectedCountry,
            selectedState,
          ),
        );
        setForm((p) => ({ ...p, city: "" }));
      } catch (e) {
        console.error("Error loading cities:", e);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    } else if (selectedCountry) {
      setCities([]);
    }
  }, [selectedCountry, selectedState]);

  useEffect(() => {
    if (isGuest) {
      setShowNewAddressForm(true);
      setSelectedAddressId("new");
    }
  }, [isGuest]);

  /* ── User: client-side totals ── */
  const userTotals = useMemo(() => {
    if (!isUser) return null;

    const itemsTotal = items.reduce((s, i) => s + getLineSubtotal(i), 0);
    const totalWeightKg = items.reduce((s, i) => s + getLineWeightKg(i), 0);

    let deliveryFee = 0;
    let matchedRate = null;

    if (items.length > 0 && shippingRates.length > 0 && totalWeightKg > 0) {
      const shipping = calculateShippingFee(totalWeightKg, shippingRates);
      deliveryFee = shipping.fee;
      matchedRate = shipping.matchedRate;
    }

    // Promo discount
    let promoDiscount = 0;
    if (selectedPromoObj) {
      const minAmt = Number(selectedPromoObj.min_order_amount || 0);
      const active = String(selectedPromoObj.is_active) === "1";
      const start = toDate(selectedPromoObj.start_date);
      const end = toDate(selectedPromoObj.end_date);
      const now = new Date();

      if (
        active &&
        (!start || now >= start) &&
        (!end || now <= end) &&
        itemsTotal >= minAmt
      ) {
        promoDiscount =
          selectedPromoObj.type === "percentage"
            ? (itemsTotal * Number(selectedPromoObj.value || 0)) / 100
            : Number(selectedPromoObj.value || 0);
        promoDiscount = Math.min(promoDiscount, itemsTotal);
      }
    }

    // Voucher discount
    let voucherDiscount = 0;
    if (selectedVoucherObj) {
      const minP = Number(selectedVoucherObj.min_purchase || 0);
      const vFrom = toDate(selectedVoucherObj.valid_from);
      const vUntil = toDate(selectedVoucherObj.valid_until);
      const now = new Date();
      const okPivot =
        selectedVoucherObj?.pivot?.is_collected === true &&
        selectedVoucherObj?.pivot?.is_used === false;

      if (
        okPivot &&
        (!vFrom || now >= vFrom) &&
        (!vUntil || now <= vUntil) &&
        itemsTotal >= minP
      ) {
        const applyType = String(
          selectedVoucherObj.apply_type || "top",
        ).toLowerCase();
        voucherDiscount =
          selectedVoucherObj.type === "percentage"
            ? ((applyType === "shipping" ? deliveryFee : itemsTotal) *
                Number(selectedVoucherObj.value || 0)) /
              100
            : Math.min(
                Number(selectedVoucherObj.value || 0),
                applyType === "shipping" ? deliveryFee : itemsTotal,
              );
      }
    }

    const subtotal = itemsTotal + deliveryFee;
    const totalDiscounts = promoDiscount + voucherDiscount;
    const beforeTax = subtotal - totalDiscounts;
    const gstAmount = beforeTax * GST_RATE;
    const total = beforeTax + gstAmount;

    return {
      itemsTotal,
      deliveryFee,
      promoDiscount,
      voucherDiscount,
      totalWeightKg,
      matchedRate,
      subtotal,
      gstAmount,
      total,
    };
  }, [items, shippingRates, selectedPromoObj, selectedVoucherObj, isUser]);

  const buyNowGuestTotals = useMemo(() => {
    const itemsTotal = items.reduce((s, i) => s + getLineSubtotal(i), 0);
    const totalWeightKg = items.reduce((s, i) => s + getLineWeightKg(i), 0);

    const shipping = calculateShippingFee(totalWeightKg, shippingRates);
    const deliveryFee = shipping.fee || 0;

    const gstAmount = (itemsTotal + deliveryFee) * GST_RATE;
    const total = itemsTotal + deliveryFee + gstAmount;

    return {
      itemsTotal,
      deliveryFee,
      gstAmount,
      total,
      totalWeightKg,
      matchedRate: shipping.matchedRate,
    };
  }, [items, shippingRates]);

  // Single totals object used in render — server for guest, computed for user
  const totals = isGuest
    ? isBuyNow
      ? buyNowGuestTotals
      : (guestServerTotals ?? {
          itemsTotal: 0,
          deliveryFee: 0,
          gstAmount: 0,
          total: 0,
          totalWeightKg: 0,
          matchedRate: null,
        })
    : (userTotals ?? {
        itemsTotal: 0,
        deliveryFee: 0,
        promoDiscount: 0,
        voucherDiscount: 0,
        totalWeightKg: 0,
        matchedRate: null,
        subtotal: 0,
        gstAmount: 0,
        total: 0,
      });

  /* ── Form handlers (unchanged) ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (name === "country") setSelectedCountry(value);
    else if (name === "state") setSelectedState(value);
    if (errors[name])
      setErrors((p) => {
        const n = { ...p };
        delete n[name];
        return n;
      });
  };

  const handleAddressSelect = (e) => {
    const id = e.target.value;
    setSelectedAddressId(id);
    if (id === "new") {
      setShowNewAddressForm(true);
      setForm((p) => ({
        ...p,
        country: "",
        state: "",
        city: "",
        zip: "",
        address1: "",
        address2: "",
        streetNumber: "",
        label: "home",
      }));
      setSelectedCountry("");
      setSelectedState("");
    } else if (id) {
      const addr = savedAddresses.find((a) => a.id === parseInt(id));
      if (addr) {
        setShowNewAddressForm(false);
        setForm((p) => ({
          ...p,
          country: addr.country || "",
          state: addr.province || addr.state || "",
          city: addr.city || "",
          zip: addr.zip_code || addr.postal_code || "",
          address1: addr.address_line1 || "",
          address2: addr.address_line2 || "",
          streetNumber: addr.street || "",
          label: addr.type || "home",
        }));
        setSelectedCountry(addr.country || "");
        setSelectedState(addr.province || addr.state || "");
      }
    }
  };

  const handleSaveDetails = async () => {
    if (isGuest) {
      toast.success("Shipping address confirmed");
      setShowNewAddressForm(false);
      return;
    }
    if (selectedAddressId && selectedAddressId !== "new") {
      toast.success("Address selected for delivery");
      return;
    }
    const requiredMap = {
      country: "Country",
      state: "State / Province",
      city: "City",
      zip: "Zip Code",
      address1: "Address line 1",
      streetNumber: "Street Number",
    };
    const fieldErrors = Object.fromEntries(
      Object.entries(requiredMap)
        .filter(([k]) => !String(form[k] || "").trim())
        .map(([k, label]) => [k, `${label} is required`]),
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length) return;
    setSaving(true);
    try {
      const response = await createAddress({
        type: form.label || "home",
        address_line1: form.address1,
        address_line2: form.address2,
        street: form.streetNumber,
        city: form.city,
        province: form.state,
        zip_code: form.zip,
        country: form.country,
      });
      toast.success("Delivery address saved");
      const reload = await getAddresses();
      setSavedAddresses(reload?.data?.data || []);
      if (response?.data?.data?.id) {
        setSelectedAddressId(response.data.data.id.toString());
        setShowNewAddressForm(false);
      }
    } catch (e) {
      toast.error("Could not save delivery address");
    } finally {
      setSaving(false);
    }
  };

  /* ── Shipping ready checks ── */
  const guestShippingReady = useMemo(() => {
    const okAddr = [
      "country",
      "state",
      "city",
      "zip",
      "address1",
      "streetNumber",
    ].every((k) => String(form[k] || "").trim());
    const okContact =
      String(guestContact.contact_name || "").trim() &&
      String(guestContact.contact_email || "").trim() &&
      String(guestContact.contact_phone || "").trim();
    return okAddr && !!okContact;
  }, [form, guestContact]);

  const userShippingReady = useMemo(
    () =>
      !!selectedAddressId && selectedAddressId !== "new" && !showNewAddressForm,
    [selectedAddressId, showNewAddressForm],
  );

  const shippingReady = isGuest ? guestShippingReady : userShippingReady;

  /* ══════════════════════════════════════════════
     Place Order
  ══════════════════════════════════════════════ */
  const handlePlaceOrder = async () => {
    if (!requireGuestOrUser("Please login or continue as guest to place order"))
      return;
    if (items.length === 0) {
      toast.error("No items selected. Go back to cart.");
      return;
    }
    if (!shippingReady) {
      toast.warning(
        isGuest
          ? "Please fill contact + shipping details"
          : "Please select a delivery address",
      );
      return;
    }

    setPlacing(true);

    try {
      /* ── Authenticated user ─────────────────────────── */
      if (isUser) {
        // Create order server-side, then navigate to payment
        const res = await http.post("/api/customer/orders", {
          shipping_address_id: parseInt(selectedAddressId, 10),
          items: items.map((i) => ({
            product_id: getProductId(i),
            quantity: getQty(i),
            color: i.color || null,
            size: i.size || null,
          })),
          total_amount: Number(userTotals.total || 0),
          total_shipping_amount: Number(userTotals.deliveryFee || 0),
          promocode_id: selectedPromoObj?.id
            ? Number(selectedPromoObj.id)
            : null,
          voucher_id: selectedVoucherObj?.id
            ? Number(selectedVoucherObj.id)
            : null,
        });

        const data = res?.data?.data;
        const createdOrder = Array.isArray(data) ? data[0] : data;

        if (!createdOrder?.id) {
          throw new Error("Order ID not received from server");
        }

        toast.success("Order created! Proceeding to payment…");

        navigate("/payment", {
          state: {
            items,
            totals: userTotals,
            orderId: createdOrder.id,
            createdOrder,
            shippingAddressId: selectedAddressId,
            selectedPromoCode: selectedPromoObj || null,
            selectedVoucher: selectedVoucherObj || null,
          },
        });
        return;
      }
      if (isGuest && isBuyNow) {
        const guestItems = items.map((it) => ({
          product_id: getProductId(it),
          quantity: getQty(it),
          color: it.color || null,
          size: it.size || null,
        }));

        const payload = {
          items: guestItems,
          contact_name: guestContact.contact_name.trim(),
          contact_email: guestContact.contact_email.trim(),
          contact_phone: guestContact.contact_phone.trim(),
          shipping_full_address: [
            form.address1,
            form.address2,
            form.streetNumber,
          ]
            .filter(Boolean)
            .join(", "),
          shipping_city: form.city,
          shipping_state: form.state,
          shipping_postcode: form.zip,
          shipping_country: form.country,
          total_shipping_amount: totals.deliveryFee,
        };

        const res = await guestHttp.post("/api/guest/orders", payload);
        const order = res?.data?.data ?? null;

        if (!order?.id) {
          throw new Error("Order ID not received from server");
        }

        navigate("/payment", {
          state: {
            mode: "guest",
            items,
            totals,
            guestOrder: order,
            guestPayload: payload,
            isBuyNow: true,
          },
        });

        return;
      }

      /* ── Guest ──────────────────────────────────────── */
      const cartItemIds = items.map((i) => getCartItemId(i)).filter(Boolean);

      if (cartItemIds.length === 0) {
        toast.error("Missing cart item IDs. Please go back to cart.");
        return;
      }

      const payload = {
        cart_item_ids: cartItemIds,
        contact_name: guestContact.contact_name.trim(),
        contact_email: guestContact.contact_email.trim(),
        contact_phone: guestContact.contact_phone.trim(),
        shipping_full_address: [form.address1, form.address2, form.streetNumber]
          .filter(Boolean)
          .join(", "),
        shipping_city: form.city,
        shipping_state: form.state,
        shipping_postcode: form.zip,
        shipping_country: form.country,
      };

      console.log("[GUEST ORDER] Payload:", payload);

      const res = await guestHttp.post(
        "/api/guest/cart/checkout/order",
        payload,
      );
      const order = res?.data?.data ?? null;

      if (!order?.id) {
        throw new Error("Order ID not received from server");
      }

      toast.success(res?.data?.message || "Order placed successfully!");

      navigate("/payment", {
        state: {
          mode: "guest",
          items,
          totals: guestServerTotals ?? totals,
          guestOrder: order,
          guestPayload: payload,
        },
      });
    } catch (err) {
      console.error(
        "[PLACE ORDER] Failed:",
        err?.response?.data || err.message,
      );
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (err?.response?.data?.errors
          ? Object.values(err.response.data.errors).flat().join(", ")
          : null) ||
        "Failed to place order. Please try again.";
      toast.error(msg);
    } finally {
      setPlacing(false);
    }
  };

  /* ── Derived display state ── */
  const confirmedAddress = savedAddresses.find(
    (a) => a.id === parseInt(selectedAddressId),
  );
  const showConfirmedAddress =
    isUser &&
    selectedAddressId &&
    selectedAddressId !== "new" &&
    !showNewAddressForm &&
    confirmedAddress;
  const showAddressForm =
    showNewAddressForm ||
    selectedAddressId === "new" ||
    (!selectedAddressId && !loadingAddresses) ||
    isGuest;

  /* ── Render ── */
  return (
    <div>
      <div className="co-screen">
        <div className="co-container">
          <div className="co-left">
            {/* ── Shipping address card ── */}
            <div className="co-card">
              <div className="co-card-header">
                <div className="co-card-header-title">
                  <MapPinIcon /> Shipping Address
                </div>
                {showConfirmedAddress && (
                  <button
                    className="co-header-btn"
                    onClick={() => {
                      setSelectedAddressId("new");
                      setShowNewAddressForm(true);
                    }}
                  >
                    <EditIcon /> Edit
                  </button>
                )}
              </div>

              <div className="co-card-body">
                {isGuest && (
                  <div className="co-block">
                    <div className="co-block-title">
                      <UserIcon /> Contact Details
                    </div>
                    <div className="checkout-grid">
                      <div className="form-row">
                        <div className="form-field">
                          <label>Full Name</label>
                          <input
                            value={guestContact.contact_name}
                            onChange={(e) =>
                              setGuestContact((p) => ({
                                ...p,
                                contact_name: e.target.value,
                              }))
                            }
                            placeholder="e.g. Easy World"
                            required
                          />
                        </div>
                        <div className="form-field">
                          <label>Email</label>
                          <input
                            type="email"
                            value={guestContact.contact_email}
                            onChange={(e) =>
                              setGuestContact((p) => ({
                                ...p,
                                contact_email: e.target.value,
                              }))
                            }
                            placeholder="e.g. easyworldonline101@gmail.com"
                            required
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-field">
                          <label>Phone</label>
                          <input
                            value={guestContact.contact_phone}
                            onChange={(e) =>
                              setGuestContact((p) => ({
                                ...p,
                                contact_phone: e.target.value,
                              }))
                            }
                            placeholder="e.g. 9812345678"
                            required
                          />
                        </div>
                        <div className="form-field" />
                      </div>
                    </div>
                    <div className="co-block-divider" />
                    <div className="co-block-title">
                      <MapPinIcon /> Shipping Details
                    </div>
                  </div>
                )}

                {showConfirmedAddress ? (
                  <div className="co-confirmed">
                    <div className="co-confirmed-badge">
                      <CheckCircleIcon /> Confirmed
                    </div>
                    <p className="co-confirmed-name">
                      Deliver to:{" "}
                      <strong>
                        {confirmedAddress.customer_name || "Customer"}
                      </strong>
                    </p>
                    <p className="co-confirmed-addr">
                      {confirmedAddress.address_line1}
                      {confirmedAddress.address_line2 &&
                        `, ${confirmedAddress.address_line2}`}
                      {confirmedAddress.street &&
                        `, ${confirmedAddress.street}`}
                      {`, ${confirmedAddress.city}`}
                      {confirmedAddress.province &&
                        `, ${confirmedAddress.province}`}
                      {`, ${confirmedAddress.country}`}
                    </p>
                    {confirmedAddress.phone && (
                      <p className="co-confirmed-phone">
                        {confirmedAddress.phone}
                      </p>
                    )}
                    <span className="co-addr-label-badge">
                      {confirmedAddress.type || "home"}
                    </span>
                  </div>
                ) : (
                  <>
                    {isUser && (
                      <div className="co-addr-select-wrap">
                        <label className="co-field-label">
                          Select Delivery Address
                        </label>
                        <select
                          className="co-select"
                          value={selectedAddressId}
                          onChange={handleAddressSelect}
                          disabled={loadingAddresses}
                        >
                          <option value="">
                            {loadingAddresses
                              ? "Loading addresses…"
                              : "Select an address or add new"}
                          </option>
                          {savedAddresses.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.type
                                ? `${a.type.charAt(0).toUpperCase() + a.type.slice(1)} — `
                                : ""}
                              {a.address_line1}, {a.city}
                              {a.province ? `, ${a.province}` : ""}
                            </option>
                          ))}
                          <option value="new">+ Add New Address</option>
                        </select>
                      </div>
                    )}

                    {showAddressForm && (
                      <div className="checkout-grid">
                        <div className="form-row">
                          <div className="form-field">
                            <label>Country</label>
                            <LocationSelect
                              name="country"
                              value={form.country}
                              onChange={(v) =>
                                handleChange({
                                  target: { name: "country", value: v },
                                })
                              }
                              options={countries}
                              placeholder={
                                loadingCountries ? "Loading…" : "Select Country"
                              }
                              isDisabled={loadingCountries}
                            />
                            {errors.country && (
                              <div className="field-error">
                                {errors.country}
                              </div>
                            )}
                          </div>
                          <div className="form-field">
                            <label>State / Province</label>
                            <LocationSelect
                              name="state"
                              value={form.state}
                              onChange={(v) =>
                                handleChange({
                                  target: { name: "state", value: v },
                                })
                              }
                              options={states}
                              placeholder={
                                !selectedCountry
                                  ? "Select Country first"
                                  : loadingStates
                                    ? "Loading…"
                                    : "Select State / Province"
                              }
                              isDisabled={!selectedCountry || loadingStates}
                            />
                            {errors.state && (
                              <div className="field-error">{errors.state}</div>
                            )}
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-field">
                            <label>City</label>
                            {cities.length > 0 ? (
                              <LocationSelect
                                name="city"
                                value={form.city}
                                onChange={(v) =>
                                  handleChange({
                                    target: { name: "city", value: v },
                                  })
                                }
                                options={cities}
                                placeholder={
                                  !selectedState
                                    ? "Select State first"
                                    : loadingCities
                                      ? "Loading…"
                                      : "Select City"
                                }
                                isDisabled={!selectedState || loadingCities}
                              />
                            ) : (
                              <input
                                name="city"
                                value={form.city}
                                onChange={handleChange}
                                placeholder={
                                  !selectedState
                                    ? "Select State first"
                                    : "Enter city name"
                                }
                                disabled={!selectedState}
                                aria-invalid={!!errors.city}
                              />
                            )}
                            {errors.city && (
                              <div className="field-error">{errors.city}</div>
                            )}
                          </div>
                          <div className="form-field">
                            <label>Zip Code</label>
                            <input
                              name="zip"
                              value={form.zip}
                              onChange={handleChange}
                              placeholder="Enter zip / postal code"
                              aria-invalid={!!errors.zip}
                            />
                            {errors.zip && (
                              <div className="field-error">{errors.zip}</div>
                            )}
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-field">
                            <label>Address Line 1</label>
                            <input
                              name="address1"
                              value={form.address1}
                              onChange={handleChange}
                              placeholder="Building, street address"
                              aria-invalid={!!errors.address1}
                            />
                            {errors.address1 && (
                              <div className="field-error">
                                {errors.address1}
                              </div>
                            )}
                          </div>
                          <div className="form-field">
                            <label>
                              Address Line 2{" "}
                              <span className="co-optional">(optional)</span>
                            </label>
                            <input
                              name="address2"
                              value={form.address2}
                              onChange={handleChange}
                              placeholder="Apartment, floor, unit…"
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-field">
                            <label>Street Number</label>
                            <input
                              name="streetNumber"
                              value={form.streetNumber}
                              onChange={handleChange}
                              placeholder="Street number"
                              aria-invalid={!!errors.streetNumber}
                            />
                            {errors.streetNumber && (
                              <div className="field-error">
                                {errors.streetNumber}
                              </div>
                            )}
                          </div>
                          <div className="form-field">
                            <label>Address Label</label>
                            <select
                              name="label"
                              value={form.label}
                              onChange={handleChange}
                            >
                              <option value="home">🏠 Home</option>
                              <option value="office">🏢 Office</option>
                              <option value="other">📍 Other</option>
                            </select>
                          </div>
                        </div>
                        <div className="co-save-row">
                          <button
                            className="co-save-btn"
                            onClick={handleSaveDetails}
                            disabled={saving}
                          >
                            {saving
                              ? "Saving…"
                              : isGuest
                                ? "Confirm Address"
                                : selectedAddressId &&
                                    selectedAddressId !== "new"
                                  ? "Confirm Address"
                                  : "Save Address"}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── Order items card ── */}
            <div className="co-card">
              <div className="co-card-header">
                <div className="co-card-header-title">
                  <PackageIcon /> Order Items
                </div>
                <span className="co-count-badge">
                  {items.length} {items.length === 1 ? "item" : "items"}
                </span>
              </div>

              {items.length === 0 ? (
                <div className="co-empty">
                  <p>No items selected.</p>
                  <button
                    className="co-back-btn"
                    onClick={() => navigate("/cart")}
                  >
                    ← Back to Cart
                  </button>
                </div>
              ) : (
                <div className="co-items-list">
                  {/* Group items by vendorId */}
                  {Object.values(
                    items.reduce((groups, item) => {
                      const key = String(
                        item.vendorId ?? item.vendor_id ?? "default",
                      );
                      if (!groups[key]) {
                        groups[key] = {
                          vendorId: item.vendorId ?? item.vendor_id,
                          storeName: item.unique_name ?? "Store",
                          // unique_name: item.unique_name ?? null,
                          items: [],
                        };
                      }
                      groups[key].items.push(item);
                      return groups;
                    }, {}),
                  ).map((group) => (
                    <div
                      key={String(group.vendorId ?? "default")}
                      className="co-vendor-group"
                    >
                      {/* Vendor header */}
                      <div className="co-vendor-header">
                        <PackageIcon />
                        <span className="co-vendor-name">
                          {group.storeName}
                        </span>
                        {group.unique_name && (
                          <span className="co-vendor-handle">
                            @{group.unique_name}
                          </span>
                        )}
                        <span className="co-vendor-count">
                          {group.items.length}{" "}
                          {group.items.length === 1 ? "item" : "items"}
                        </span>
                      </div>

                      {/* Items under this vendor */}
                      {group.items.map((item) => {
                        const weightKg = (() => {
                          const w = Number(
                            item.weight ?? item.product?.weight ?? 0,
                          );
                          const unit = String(
                            item.weight_unit ??
                              item.product?.weight_unit ??
                              "kg",
                          ).toLowerCase();
                          if (!w || isNaN(w)) return null;
                          if (
                            unit === "g" ||
                            unit === "gram" ||
                            unit === "grams"
                          )
                            return { value: w, display: `${w} g` };
                          return { value: w, display: `${w} kg` };
                        })();

                        return (
                          <div key={item.id} className="co-item">
                            <div className="co-item-img">
                              <img
                                src={item.image || ""}
                                alt={item.name}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            </div>

                            <div className="co-item-info">
                              <p className="co-item-name">{item.name}</p>

                              <div className="co-item-tags">
                                {item.brand && (
                                  <span className="co-tag">{item.brand}</span>
                                )}
                                {item.color && (
                                  <span className="co-tag">
                                    🎨 {item.color}
                                  </span>
                                )}
                                {item.size && (
                                  <span className="co-tag">📐 {item.size}</span>
                                )}
                                {weightKg && (
                                  <span className="co-tag co-tag--weight">
                                    ⚖️ {weightKg.display}
                                  </span>
                                )}
                              </div>

                              <p className="co-item-price">
                                {toCurrency(getLineSubtotal(item))}
                              </p>
                            </div>

                            <div className="co-item-qty-wrap">
                              <span className="co-qty-label">Qty</span>
                              <span className="co-qty-pill">
                                {item.quantity}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Order summary sidebar ── */}
          <div className="order-summary">
            <div className="order-summary-header">
              <ReceiptIcon />
              <span className="order-summary-header-title">Order Summary</span>
            </div>

            <div className="order-details">
              {isGuest && loadingGuestTotals ? (
                <div className="co-calc" style={{ padding: "1rem 0" }}>
                  Calculating totals…
                </div>
              ) : (
                <>
                  <div className="order-row">
                    <span className="order-label">Items Total</span>
                    <span className="order-value">
                      {toCurrency(totals.itemsTotal)}
                    </span>
                  </div>

                  {/* Weight row — only meaningful for users (server handles it for guests) */}
                  {isUser && (
                    <div className="order-row">
                      <span className="order-label">Total Weight</span>
                      <span className="order-value">
                        {Number(totals.totalWeightKg || 0).toFixed(3)} kg
                      </span>
                    </div>
                  )}

                  <div className="order-row">
                    <span className="order-label">
                      Delivery Fee
                      {isUser && totals.matchedRate && (
                        <span className="co-fee-sub">
                          {totals.matchedRate.weight_range} kg
                          {totals.matchedRate.delivery_type
                            ? ` · ${totals.matchedRate.delivery_type}`
                            : ""}
                        </span>
                      )}
                    </span>
                    <span className="order-value">
                      {loadingShipping ? (
                        <span className="co-calc">Calculating…</span>
                      ) : (
                        toCurrency(totals.deliveryFee)
                      )}
                    </span>
                  </div>

                  {/* Promo + voucher selectors — user only */}
                  {isUser && (
                    <div className="co-discounts-block">
                      <div className="co-discount-row">
                        <label className="co-discount-label">
                          <TagIcon /> Promo Code
                        </label>
                        <select
                          className="co-discount-select"
                          value={selectedPromoId}
                          onChange={(e) => setSelectedPromoId(e.target.value)}
                        >
                          <option value="">No Promo</option>
                          {availablePromoCodes.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.code} (
                              {p.type === "percentage"
                                ? `${p.value}%`
                                : toCurrency(p.value)}
                              ) — Min {toCurrency(p.min_order_amount)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="co-discount-row">
                        <label className="co-discount-label">
                          <TagIcon /> Voucher
                        </label>
                        <select
                          className="co-discount-select"
                          value={selectedVoucherId}
                          onChange={(e) => setSelectedVoucherId(e.target.value)}
                        >
                          <option value="">No Voucher</option>
                          {availableShippingVouchers.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.code} (
                              {v.type === "percentage"
                                ? `${v.value}%`
                                : toCurrency(v.value)}
                              ) — Min {toCurrency(v.min_purchase)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {totals.promoDiscount > 0 && selectedPromoObj && (
                        <div className="order-row co-saving-row">
                          <span className="order-label">
                            Promo{" "}
                            <span className="co-code-chip co-chip-green">
                              {selectedPromoObj.code}
                            </span>
                          </span>
                          <span className="order-value co-green">
                            −{toCurrency(totals.promoDiscount)}
                          </span>
                        </div>
                      )}

                      {totals.voucherDiscount > 0 && selectedVoucherObj && (
                        <div className="order-row co-saving-row">
                          <span className="order-label">
                            Voucher{" "}
                            <span className="co-code-chip co-chip-blue">
                              {selectedVoucherObj.code}
                            </span>
                          </span>
                          <span className="order-value co-blue">
                            −{toCurrency(totals.voucherDiscount)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="order-row">
                    <span className="order-label">GST (10%)</span>
                    <span className="order-value">
                      {toCurrency(totals.gstAmount)}
                    </span>
                  </div>

                  <div className="order-row total-row">
                    <span className="order-label">Total</span>
                    <span className="order-value total-amount">
                      {toCurrency(totals.total)}
                    </span>
                  </div>
                </>
              )}

              <button
                className="checkout-btn"
                onClick={handlePlaceOrder}
                disabled={
                  items.length === 0 ||
                  !shippingReady ||
                  placing ||
                  loadingGuestTotals
                }
              >
                {placing ? "Placing Order…" : "Place Order"}
              </button>

              {!shippingReady && items.length > 0 && (
                <p className="co-hint">
                  {isGuest
                    ? "Fill contact + shipping details to continue"
                    : "Select or add a delivery address to continue"}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CheckoutPage;
